import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, Upload, Calendar, FileText, Sparkles, 
  Check, X, AlertCircle, Loader2, Receipt, CheckCircle2,
  Clock, Flag, Plus, ArrowRight, Shield, Camera, ClipboardCheck,
  Image, Download, Eye, FileCheck, AlertTriangle, RefreshCw,
  Pencil, Trash2, Save, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { apiService, api } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';

export default function PhasePaymentDialog({ 
  isOpen, 
  onClose, 
  phase, 
  onPaymentAdded,
  existingPayments = []
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentType, setPaymentType] = useState('partial');
  const [skipReceipt, setSkipReceipt] = useState(false);
  const [showReceiptUploader, setShowReceiptUploader] = useState(false);
  
  // Legacy bypass state
  const [showBypassConfirm, setShowBypassConfirm] = useState(false);
  const [bypassReason, setBypassReason] = useState('');
  const [isBypassingPayment, setIsBypassingPayment] = useState(false);
  
  // Compliance state
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [complianceDocuments, setComplianceDocuments] = useState(null);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);
  const [showInvoiceAmountInput, setShowInvoiceAmountInput] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [isRequestingCompliance, setIsRequestingCompliance] = useState(false);
  const [showComplianceDocuments, setShowComplianceDocuments] = useState(false);
  const [isUploadingW9, setIsUploadingW9] = useState(false);
  const [w9File, setW9File] = useState(null);
  const [isUploadingCOI, setIsUploadingCOI] = useState(false);
  const [coiFile, setCoiFile] = useState(null);
  const [coiExpiration, setCoiExpiration] = useState('');
  // New: Lien Waiver upload on behalf of contractor
  const [isUploadingLienWaiver, setIsUploadingLienWaiver] = useState(false);
  const [lienWaiverFile, setLienWaiverFile] = useState(null);
  // New: Request new lien waiver
  const [showNewLienWaiverInput, setShowNewLienWaiverInput] = useState(false);
  const [newLienWaiverAmount, setNewLienWaiverAmount] = useState('');
  const [isRequestingNewLienWaiver, setIsRequestingNewLienWaiver] = useState(false);
  // New: Completion Photos upload on behalf of contractor
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  
  // Edit/Delete payment state
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editingPaymentData, setEditingPaymentData] = useState({ amount: '' });
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    notes: ''
  });

  const wasMarkedFinal = phase?.payment_status === 'final' || phase?.is_final_payment;
  const isLaborPhase = (phase?.phase_type === 'labor' || phase?.phase_type === 'labor_material') && phase?.contractor_id;

  const loadPayments = useCallback(async () => {
    if (!phase?.id) return;
    try {
      const res = await apiService.getPhasePayments(phase.id);
      setPayments(res.data?.payments || []);
    } catch (e) {
      console.error('Failed to load payments:', e);
    }
  }, [phase?.id]);

  const loadComplianceStatus = useCallback(async () => {
    if (!phase?.id || !isLaborPhase) return;
    try {
      setIsLoadingCompliance(true);
      const res = await apiService.getPhaseComplianceStatus(phase.id);
      console.log('Compliance status loaded:', JSON.stringify(res.data, null, 2));
      setComplianceStatus(res.data);
    } catch (e) {
      console.error('Failed to load compliance status:', e);
    } finally {
      setIsLoadingCompliance(false);
    }
  }, [phase?.id, isLaborPhase]);

  // Request compliance documents from contractor with invoice amount
  const handleRequestComplianceDocs = async () => {
    if (!phase?.id) return;
    
    const amount = invoiceAmount ? parseFloat(invoiceAmount) : null;
    if (invoiceAmount && (isNaN(amount) || amount <= 0)) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    // Calculate if this is a partial or final payment
    const phaseTotal = phase?.actual_cost || phase?.estimated_cost || 0;
    const alreadyPaid = totalPaid || 0;
    const thisPayment = amount || 0;
    const afterPayment = alreadyPaid + thisPayment;
    const isPartialPayment = afterPayment < phaseTotal;
    const remainingAfterPayment = Math.max(0, phaseTotal - afterPayment);
    
    setIsRequestingCompliance(true);
    try {
      const res = await apiService.requestComplianceDocs(phase.id, amount, {
        is_partial: isPartialPayment,
        total_phase_cost: phaseTotal,
        already_paid: alreadyPaid,
        remaining_after_payment: remainingAfterPayment
      });
      toast.success(`Compliance request sent to contractor${res.data?.contractor_email ? ` (${res.data.contractor_email})` : ''}`);
      setShowInvoiceAmountInput(false);
      setInvoiceAmount('');
      // Reload compliance status to show updated state
      loadComplianceStatus();
    } catch (e) {
      const errorMessage = typeof e.response?.data?.detail === 'string' 
        ? e.response?.data?.detail 
        : (e.response?.data?.detail?.[0]?.msg || 'Failed to send compliance request');
      toast.error(errorMessage);
    } finally {
      setIsRequestingCompliance(false);
    }
  };

  // Handle W9 upload
  const handleW9Upload = async (file) => {
    if (!file || !complianceStatus?.compliance?.w9?.contractor_id) {
      toast.error('Please select a W-9 file to upload');
      return;
    }

    const contractorId = complianceStatus.compliance.w9.contractor_id;
    setIsUploadingW9(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiService.uploadContractorW9(contractorId, formData);
      toast.success('W-9 uploaded successfully');
      setW9File(null);
      // Reload compliance status to show updated W9 status
      loadComplianceStatus();
    } catch (e) {
      const errorMessage = typeof e.response?.data?.detail === 'string' 
        ? e.response?.data?.detail 
        : (e.response?.data?.detail?.[0]?.msg || 'Failed to upload W-9');
      toast.error(errorMessage);
    } finally {
      setIsUploadingW9(false);
    }
  };

  // Handle COI upload
  const handleCOIUpload = async (file) => {
    if (!file || !complianceStatus?.compliance?.coi?.contractor_id) {
      toast.error('Please select a COI file to upload');
      return;
    }

    const contractorId = complianceStatus.compliance.coi.contractor_id;
    setIsUploadingCOI(true);
    
    try {
      // v2.4: COI expiration is MANDATORY (backend enforces too)
      if (!coiExpiration) {
        toast.error('Please enter the COI expiration date before uploading.');
        setIsUploadingCOI(false);
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('expiration_date', coiExpiration);
      await apiService.uploadContractorCOI(contractorId, formData);
      toast.success('Certificate of Insurance uploaded successfully');
      setCoiFile(null);
      setCoiExpiration('');
      // Reload compliance status
      loadComplianceStatus();
    } catch (e) {
      const errorMessage = typeof e.response?.data?.detail === 'string' 
        ? e.response?.data?.detail 
        : (e.response?.data?.detail?.[0]?.msg || 'Failed to upload COI');
      toast.error(errorMessage);
    } finally {
      setIsUploadingCOI(false);
    }
  };

  // Handle Lien Waiver upload on behalf of contractor
  const handleLienWaiverUpload = async (file) => {
    if (!file || !phase?.id) {
      toast.error('Please select a lien waiver file to upload');
      return;
    }

    setIsUploadingLienWaiver(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiService.uploadPhaseLienWaiver(phase.id, formData);
      toast.success('Lien waiver uploaded successfully');
      setLienWaiverFile(null);
      // Reload compliance status and documents
      loadComplianceStatus();
      loadComplianceDocuments();
    } catch (e) {
      const errorMessage = typeof e.response?.data?.detail === 'string' 
        ? e.response?.data?.detail 
        : (e.response?.data?.detail?.[0]?.msg || 'Failed to upload lien waiver');
      toast.error(errorMessage);
    } finally {
      setIsUploadingLienWaiver(false);
    }
  };

  // Handle Request New Lien Waiver (replaces existing one)
  const handleRequestNewLienWaiver = async () => {
    if (!phase?.id) return;
    
    const amount = parseFloat(newLienWaiverAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    setIsRequestingNewLienWaiver(true);
    try {
      const res = await apiService.requestNewLienWaiver(phase.id, amount);
      toast.success(`New lien waiver request sent to ${res.data?.contractor_email || 'contractor'} for $${amount.toLocaleString()}`);
      setShowNewLienWaiverInput(false);
      setNewLienWaiverAmount('');
      // Reload compliance status to show updated state
      loadComplianceStatus();
    } catch (e) {
      const errorMessage = typeof e.response?.data?.detail === 'string' 
        ? e.response?.data?.detail 
        : (e.response?.data?.detail?.[0]?.msg || 'Failed to send new lien waiver request');
      toast.error(errorMessage);
    } finally {
      setIsRequestingNewLienWaiver(false);
    }
  };

  // Handle Completion Photos upload on behalf of contractor
  const handlePhotosUpload = async (files) => {
    if (!files || files.length === 0 || !phase?.id) {
      toast.error('Please select completion photos to upload');
      return;
    }

    setIsUploadingPhotos(true);
    
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      await apiService.uploadPhaseCompletionPhotos(phase.id, formData);
      toast.success(`${files.length} completion photo(s) uploaded successfully`);
      setPhotoFiles([]);
      // Reload compliance status and documents
      loadComplianceStatus();
      loadComplianceDocuments();
    } catch (e) {
      const errorMessage = typeof e.response?.data?.detail === 'string' 
        ? e.response?.data?.detail 
        : (e.response?.data?.detail?.[0]?.msg || 'Failed to upload completion photos');
      toast.error(errorMessage);
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  // Load compliance documents (photos, lien waiver, inspection)
  const loadComplianceDocuments = useCallback(async () => {
    if (!phase?.id || !isLaborPhase) return;
    try {
      console.log('Loading compliance documents for phase:', phase.id);
      const res = await apiService.getPhaseComplianceDocuments(phase.id);
      console.log('Compliance documents API response:', res.data);
      if (res.data) {
        setComplianceDocuments(res.data);
      }
    } catch (e) {
      console.error('Failed to load compliance documents:', e);
    }
  }, [phase?.id, isLaborPhase]);

  useEffect(() => {
    if (isOpen && phase?.id) {
      loadPayments();
      loadComplianceStatus();
      loadComplianceDocuments();
      setPaymentType(wasMarkedFinal ? 'additional' : 'partial');
      setSkipReceipt(false);
      setReceiptFile(null);
      setExtractedData(null);
      setShowInvoiceAmountInput(false);
      // Auto-expand compliance documents section when viewing a paid phase
      setShowComplianceDocuments(phase?.is_paid || false);
      setInvoiceAmount('');
      setPaymentData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'check',
        notes: ''
      });
    }
  }, [isOpen, phase?.id, phase?.is_paid, loadPayments, loadComplianceStatus, loadComplianceDocuments, wasMarkedFinal]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setReceiptFile(file);
    setIsExtracting(true);
    setExtractedData(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiService.extractReceiptData(formData);
      
      if (res.data?.success && res.data?.extracted_data) {
        const data = res.data.extracted_data;
        setExtractedData(data);
        
        if (data.total_amount) {
          setPaymentData(prev => ({
            ...prev,
            amount: String(data.total_amount)
          }));
        }
        if (data.date) {
          const dateStr = data.date;
          if (dateStr && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            setPaymentData(prev => ({
              ...prev,
              payment_date: dateStr
            }));
          }
        }
        if (data.description) {
          setPaymentData(prev => ({
            ...prev,
            notes: data.description
          }));
        }
        
        toast.success('Receipt data extracted successfully!');
      } else {
        toast.info('Could not extract data. Please enter manually.');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error('Failed to extract receipt data');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSubmit = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (wasMarkedFinal && paymentType === 'additional' && !receiptFile && !skipReceipt) {
      toast.error('Additional payments after "Final" require a receipt for audit compliance, or toggle "Bypass Receipt Requirement"');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        amount: parseFloat(paymentData.amount),
        payment_date: paymentData.payment_date,
        payment_method: paymentData.payment_method,
        notes: paymentData.notes,
        is_final_payment: paymentType === 'final',
        is_additional_payment: paymentType === 'additional',
        skip_receipt: skipReceipt,
        receipt_extracted_amount: extractedData?.total_amount || null
      };

      const res = await apiService.addPhasePayment(phase.id, payload);
      
      let paymentTypeLabel = 'Payment';
      if (paymentType === 'final') paymentTypeLabel = 'Final payment';
      else if (paymentType === 'partial') paymentTypeLabel = 'Partial payment';
      else if (paymentType === 'additional') paymentTypeLabel = 'Additional payment';
      
      // Show appropriate message based on payment status
      const newTotal = res.data.new_total || 0;
      const remaining = res.data.remaining || 0;
      const isFullyPaid = res.data.is_fully_paid;
      
      if (isFullyPaid) {
        toast.success(`${paymentTypeLabel} of $${parseFloat(paymentData.amount).toLocaleString()} logged. Phase fully paid! Total: $${newTotal.toLocaleString()}`);
      } else {
        toast.success(`${paymentTypeLabel} of $${parseFloat(paymentData.amount).toLocaleString()} logged. Paid so far: $${newTotal.toLocaleString()} | Remaining: $${remaining.toLocaleString()}`);
      }
      
      setPaymentData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'check',
        notes: ''
      });
      setReceiptFile(null);
      setExtractedData(null);
      setSkipReceipt(false);
      setPaymentType(wasMarkedFinal ? 'additional' : 'partial');
      
      await loadPayments();
      if (onPaymentAdded) {
        onPaymentAdded(res.data);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to log payment');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle legacy bypass - mark as paid without compliance
  const handleBypassPayment = async () => {
    if (!bypassReason.trim()) {
      toast.error('Please provide a reason for bypassing compliance');
      return;
    }

    setIsBypassingPayment(true);
    try {
      const res = await apiService.markPhaseAsPaidBypass(phase.id, {
        reason: bypassReason,
        amount: phase.estimated_labor || phase.estimated_cost || phase.actual_cost || 0,
        payment_date: new Date().toISOString().split('T')[0]
      });
      
      toast.success('Item marked as paid (Legacy Bypass)');
      setShowBypassConfirm(false);
      setBypassReason('');
      
      if (onPaymentAdded) {
        onPaymentAdded(res.data);
      }
      onClose();
    } catch (error) {
      console.error('Bypass payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setIsBypassingPayment(false);
    }
  };

  // Handle edit payment
  const handleEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setEditingPaymentData({ amount: String(payment.amount) });
  };

  const handleSavePaymentEdit = async () => {
    if (!editingPaymentId) return;
    
    const newAmount = parseFloat(editingPaymentData.amount);
    if (isNaN(newAmount) || newAmount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsUpdatingPayment(true);
    try {
      await apiService.updatePhasePayment(editingPaymentId, { amount: newAmount });
      toast.success('Payment updated successfully');
      setEditingPaymentId(null);
      setEditingPaymentData({ amount: '' });
      await loadPayments();
      if (onPaymentAdded) {
        onPaymentAdded({ updated: true });
      }
    } catch (error) {
      console.error('Update payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPaymentId(null);
    setEditingPaymentData({ amount: '' });
  };

  // Handle delete payment
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record? This cannot be undone.')) {
      return;
    }

    setIsDeletingPayment(true);
    try {
      await apiService.deletePhasePayment(paymentId);
      toast.success('Payment deleted successfully');
      await loadPayments();
      if (onPaymentAdded) {
        onPaymentAdded({ deleted: true });
      }
    } catch (error) {
      console.error('Delete payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setIsDeletingPayment(false);
    }
  };

  const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  // Use actual_cost as the total owed, falling back to estimated_cost
  const totalOwed = phase?.actual_cost || phase?.estimated_cost || phase?.estimated_labor || 0;
  const remaining = totalOwed - totalPaid;
  const newAmount = parseFloat(paymentData.amount) || 0;
  const projectedTotal = totalPaid + newAmount;
  
  // Only treat as "view-only" mode if paid AND no remaining balance
  const isViewingPaidPhase = phase?.is_paid && totalPaid > 0 && remaining <= 0;

  // PART A: Self-heal — phase is fully paid but is_paid flag is stuck on false.
  // Happens when (a) a partial payment was edited to Final but didn't cascade,
  // (b) actual_cost was raised then lowered, or (c) the original POST silently
  // failed to update the phase doc. One click here will reconcile the state.
  const isStuckUnpaid = !phase?.is_paid && totalPaid > 0 && remaining <= 0 && totalOwed > 0;
  const [isHealing, setIsHealing] = useState(false);

  const handleMarkFullyPaid = async () => {
    if (!phase?.id) return;
    setIsHealing(true);
    try {
      await api.post(`/phases/${phase.id}/mark-fully-paid`, {});
      toast.success("Marked fully paid. Phase is now complete.");
      if (typeof onPaymentAdded === 'function') onPaymentAdded();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to mark fully paid';
      toast.error(detail);
    } finally {
      setIsHealing(false);
    }
  };

  // Compliance is informational - not blocking for payments
  // Compliance is enforced at draw request level
  const hasComplianceIssues = isLaborPhase && complianceStatus && !complianceStatus.can_pay;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            {isViewingPaidPhase ? (
              <>
                <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                <span className="truncate">Compliance Documents - {phase?.name}</span>
              </>
            ) : (
              <>
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                <span className="truncate">Log Payment - {phase?.name}</span>
              </>
            )}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
            {phase?.phase_type === 'labor' 
              ? (phase?.contractor_name ? `Contractor: ${phase.contractor_name}` : 'No contractor assigned')
              : (phase?.supplier_vendor ? `Supplier: ${phase.supplier_vendor}` : `${phase?.phase_type === 'material' ? 'Material' : 'Soft Cost'} Phase`)
            }
            {isViewingPaidPhase && (
              <Badge className="bg-green-100 text-green-700 text-xs">
                <Check className="w-3 h-3 mr-1" />
                Fully Paid
              </Badge>
            )}
            {/* Show partial payment status if some has been paid but more is owed */}
            {!isViewingPaidPhase && totalPaid > 0 && remaining > 0 && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">
                <DollarSign className="w-3 h-3 mr-1" />
                ${totalPaid.toLocaleString()} paid / ${remaining.toLocaleString()} remaining
              </Badge>
            )}
            {wasMarkedFinal && !isViewingPaidPhase && (
              <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-xs">
                <Flag className="w-3 h-3 mr-1" />
                Previously marked Final
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* PART A: Self-heal banner for stuck-unpaid phases */}
        {isStuckUnpaid && (
          <div
            className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 mb-3"
            data-testid="stuck-unpaid-heal-banner"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-900 text-sm">
                  This phase is fully paid — let's finalize it.
                </p>
                <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
                  You've recorded <strong>${totalPaid.toLocaleString()}</strong> in payments against
                  a <strong>${totalOwed.toLocaleString()}</strong> total. The "Final" checkbox didn't
                  cascade last time — one click below fixes the status so it counts toward
                  Total Paid on your dashboard.
                </p>
                <Button
                  onClick={handleMarkFullyPaid}
                  disabled={isHealing}
                  className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                  size="sm"
                  data-testid="mark-fully-paid-btn"
                >
                  {isHealing ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Finalizing…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark fully paid &amp; complete</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Workflow Steps - Only show for labor phases that aren't fully paid */}
        {isLaborPhase && !isViewingPaidPhase && (
          <div className="border rounded-lg p-3 bg-gradient-to-r from-slate-50 to-blue-50 mb-2">
            <p className="text-xs font-medium text-slate-700 mb-2">Payment Workflow</p>
            <div className="flex items-center justify-between text-xs">
              {/* Step 1 */}
              <div className={`flex flex-col items-center flex-1 ${
                !complianceStatus?.compliance_request_sent ? 'text-blue-600 font-medium' : 'text-green-600'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${
                  !complianceStatus?.compliance_request_sent 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-green-600 text-white'
                }`}>
                  {complianceStatus?.compliance_request_sent ? <Check className="w-3 h-3" /> : '1'}
                </div>
                <span className="text-center leading-tight">Request<br/>Docs</span>
              </div>
              
              {/* Arrow */}
              <div className="flex-shrink-0 text-slate-300 px-1">→</div>
              
              {/* Step 2 */}
              <div className={`flex flex-col items-center flex-1 ${
                complianceStatus?.compliance_request_sent && !complianceStatus?.can_pay 
                  ? 'text-amber-600 font-medium' 
                  : complianceStatus?.can_pay ? 'text-green-600' : 'text-slate-400'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${
                  complianceStatus?.can_pay 
                    ? 'bg-green-600 text-white' 
                    : complianceStatus?.compliance_request_sent 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-slate-200 text-slate-500'
                }`}>
                  {complianceStatus?.can_pay ? <Check className="w-3 h-3" /> : '2'}
                </div>
                <span className="text-center leading-tight">Contractor<br/>Signs</span>
              </div>
              
              {/* Arrow */}
              <div className="flex-shrink-0 text-slate-300 px-1">→</div>
              
              {/* Step 3 */}
              <div className={`flex flex-col items-center flex-1 ${
                complianceStatus?.can_pay && totalPaid === 0 ? 'text-blue-600 font-medium' : 
                totalPaid > 0 ? 'text-green-600' : 'text-slate-400'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${
                  totalPaid > 0 && remaining <= 0
                    ? 'bg-green-600 text-white' 
                    : complianceStatus?.can_pay 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-200 text-slate-500'
                }`}>
                  {totalPaid > 0 && remaining <= 0 ? <Check className="w-3 h-3" /> : '3'}
                </div>
                <span className="text-center leading-tight">Record<br/>Payment</span>
              </div>
            </div>
            
            {/* Current step explanation */}
            <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600">
              {!complianceStatus?.compliance_request_sent && (
                <p>→ <strong>Step 1:</strong> Enter payment amount and request compliance documents from contractor</p>
              )}
              {complianceStatus?.compliance_request_sent && !complianceStatus?.can_pay && (
                <p>→ <strong>Step 2:</strong> Waiting for contractor to sign lien waiver and upload photos</p>
              )}
              {complianceStatus?.can_pay && remaining > 0 && (
                <p>→ <strong>Step 3:</strong> Documents received! Enter payment amount and click "Record Payment"</p>
              )}
              {remaining <= 0 && totalPaid > 0 && (
                <p>✓ <strong>Complete:</strong> All payments recorded for this phase</p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
          {/* Compliance Status Section - Only for labor phases with contractors */}
          {isLaborPhase && (
            <div className="border rounded-lg p-3 sm:p-4 border-blue-200 bg-blue-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-xs sm:text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Compliance Status
                </h4>
                {isLoadingCompliance ? (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                ) : (
                  <Badge className="bg-blue-100 text-blue-700 text-xs">Informational</Badge>
                )}
              </div>

              {isLoadingCompliance ? (
                <p className="text-xs sm:text-sm text-slate-500">Loading compliance status...</p>
              ) : complianceStatus ? (
                <div className="space-y-2">
                  {/* Lien Waiver */}
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                      {complianceStatus.compliance.lien_waiver.signed ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                      <span>Lien Waiver</span>
                    </div>
                    {complianceStatus.compliance.lien_waiver.signed ? (
                      <span className="text-green-600 text-xs">Signed</span>
                    ) : complianceStatus.compliance.lien_waiver.request_sent ? (
                      <span className="text-amber-600 text-xs">Awaiting</span>
                    ) : (
                      <span className="text-red-500 text-xs">Required</span>
                    )}
                  </div>

                  {/* Completion Photos */}
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                      {complianceStatus.compliance.completion_photos.uploaded ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <Camera className="w-4 h-4 text-red-500" />
                      )}
                      <span>Completion Photos</span>
                    </div>
                    {complianceStatus.compliance.completion_photos.uploaded ? (
                      <span className="text-green-600 text-xs">{complianceStatus.compliance.completion_photos.count} uploaded</span>
                    ) : complianceStatus.compliance_request_sent ? (
                      <span className="text-amber-600 text-xs">Awaiting</span>
                    ) : (
                      <span className="text-red-500 text-xs">Required</span>
                    )}
                  </div>

                  {/* W-9 Tax Form Status */}
                  {complianceStatus.compliance.w9?.required && (
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <div className="flex items-center gap-2">
                        {complianceStatus.compliance.w9.received ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                        <span>W-9 Tax Form</span>
                      </div>
                      {complianceStatus.compliance.w9.received ? (
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 text-xs">On File</span>
                          {complianceStatus.compliance.w9.file_id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/contractors/${complianceStatus.compliance.w9.contractor_id}/w9-download/${complianceStatus.compliance.w9.file_id}`;
                                  console.log('Downloading W-9:', url);
                                  try {
                                    const response = await fetch(url);
                                    if (!response.ok) {
                                      toast.error(`Failed to download W-9: ${response.status}. Click Replace to upload a new file.`);
                                      return;
                                    }
                                    const blob = await response.blob();
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = blobUrl;
                                    a.download = `W9_${complianceStatus.compliance.w9.contractor_name || 'contractor'}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(blobUrl);
                                    a.remove();
                                  } catch (e) {
                                    console.error('W-9 download error:', e);
                                    toast.error('Failed to download W-9. Click Replace to upload a new file.');
                                  }
                                }}
                                title="Download W-9"
                              >
                                <Download className="w-3 h-3 text-slate-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  // Scroll to W-9 upload section
                                  const w9Section = document.getElementById('w9-upload-section');
                                  if (w9Section) {
                                    w9Section.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    w9Section.classList.add('ring-2', 'ring-blue-500');
                                    setTimeout(() => w9Section.classList.remove('ring-2', 'ring-blue-500'), 2000);
                                  }
                                }}
                                title="Replace W-9"
                              >
                                <RefreshCw className="w-3 h-3 text-amber-600" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-amber-600" title="W-9 marked as received but file not found">No file</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-red-500 text-xs">Required</span>
                      )}
                    </div>
                  )}

                  {/* COI (Certificate of Insurance) Status - in compliance list */}
                  {complianceStatus.compliance.coi?.required && (
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <div className="flex items-center gap-2">
                        {complianceStatus.compliance.coi.received && !complianceStatus.compliance.coi.is_expired ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : complianceStatus.compliance.coi.is_expired ? (
                          <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        ) : (
                          <Shield className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                        <span>Certificate of Insurance</span>
                      </div>
                      {complianceStatus.compliance.coi.received && !complianceStatus.compliance.coi.is_expired ? (
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 text-xs">On File</span>
                          {complianceStatus.compliance.coi.file_id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/contractors/${complianceStatus.compliance.coi.contractor_id}/coi-download/${complianceStatus.compliance.coi.file_id}`;
                                  console.log('Downloading COI:', url);
                                  try {
                                    const response = await fetch(url);
                                    if (!response.ok) {
                                      toast.error(`Failed to download COI: ${response.status}. Click Replace to upload a new file.`);
                                      return;
                                    }
                                    const blob = await response.blob();
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = blobUrl;
                                    a.download = `COI_${complianceStatus.compliance.coi.contractor_name || 'contractor'}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(blobUrl);
                                    a.remove();
                                  } catch (e) {
                                    console.error('COI download error:', e);
                                    toast.error('Failed to download COI. Click Replace to upload a new file.');
                                  }
                                }}
                                title="Download COI"
                              >
                                <Download className="w-3 h-3 text-slate-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  // Scroll to COI upload section
                                  const coiSection = document.getElementById('coi-upload-section');
                                  if (coiSection) {
                                    coiSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    coiSection.classList.add('ring-2', 'ring-blue-500');
                                    setTimeout(() => coiSection.classList.remove('ring-2', 'ring-blue-500'), 2000);
                                  }
                                }}
                                title="Replace COI"
                              >
                                <RefreshCw className="w-3 h-3 text-amber-600" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-amber-600" title="COI marked as received but file not found">No file</span>
                          )}
                        </div>
                      ) : complianceStatus.compliance.coi.is_expired ? (
                        <span className="text-orange-500 text-xs">Expired</span>
                      ) : (
                        <span className="text-red-500 text-xs">Required</span>
                      )}
                    </div>
                  )}

                  {/* W-9 Upload Section - Show if W9 is required (upload or replace) */}
                  {complianceStatus.compliance.w9?.required && (
                    <div id="w9-upload-section" className="mt-3 pt-3 border-t border-blue-200 transition-all">
                      <div className={`${complianceStatus.compliance.w9?.received ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-3`}>
                        <div className="flex items-start gap-2 mb-2">
                          {complianceStatus.compliance.w9?.received ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${complianceStatus.compliance.w9?.received ? 'text-green-800' : 'text-red-800'}`}>
                              {complianceStatus.compliance.w9?.received ? 'W-9 On File' : 'W-9 Required'}
                            </p>
                            <p className={`text-xs ${complianceStatus.compliance.w9?.received ? 'text-green-600' : 'text-red-600'} mt-1`}>
                              {complianceStatus.compliance.w9?.received 
                                ? `W-9 is on file for ${complianceStatus.compliance.w9.contractor_name || 'this contractor'}.`
                                : `A W-9 form is required for ${complianceStatus.compliance.w9.contractor_name || 'this contractor'} before payment can be processed for tax reporting.`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <Label className="text-xs text-slate-600 mb-2 block">
                            {complianceStatus.compliance.w9?.received ? 'Replace W-9 Form' : 'Upload W-9 Form'}
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => setW9File(e.target.files?.[0] || null)}
                              className="flex-1 text-xs"
                              disabled={isUploadingW9}
                            />
                            <Button
                              size="sm"
                              onClick={() => w9File && handleW9Upload(w9File)}
                              disabled={!w9File || isUploadingW9}
                              className={complianceStatus.compliance.w9?.received ? "bg-slate-600 hover:bg-slate-700" : "bg-blue-600 hover:bg-blue-700"}
                            >
                              {isUploadingW9 ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-1" />
                                  {complianceStatus.compliance.w9?.received ? 'Replace' : 'Upload'}
                                </>
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Accepted: PDF, JPG, PNG</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* COI Upload Section - Show if COI is required (upload or replace) */}
                  {complianceStatus.compliance.coi?.required && (
                    <div id="coi-upload-section" className="mt-3 pt-3 border-t border-blue-200 transition-all">
                      <div className={`${complianceStatus.compliance.coi?.received && !complianceStatus.compliance.coi?.is_expired ? 'bg-green-50 border-green-200' : complianceStatus.compliance.coi?.is_expired ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'} border rounded-lg p-3`}>
                        <div className="flex items-start gap-2 mb-2">
                          {complianceStatus.compliance.coi?.received && !complianceStatus.compliance.coi?.is_expired ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertCircle className={`w-4 h-4 ${complianceStatus.compliance.coi?.is_expired ? 'text-orange-500' : 'text-red-500'} mt-0.5 flex-shrink-0`} />
                          )}
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${complianceStatus.compliance.coi?.received && !complianceStatus.compliance.coi?.is_expired ? 'text-green-800' : complianceStatus.compliance.coi?.is_expired ? 'text-orange-800' : 'text-red-800'}`}>
                              {complianceStatus.compliance.coi?.received && !complianceStatus.compliance.coi?.is_expired 
                                ? 'COI On File' 
                                : complianceStatus.compliance.coi?.is_expired 
                                  ? 'COI Expired' 
                                  : 'COI Required'}
                            </p>
                            <p className={`text-xs ${complianceStatus.compliance.coi?.received && !complianceStatus.compliance.coi?.is_expired ? 'text-green-600' : complianceStatus.compliance.coi?.is_expired ? 'text-orange-600' : 'text-red-600'} mt-1`}>
                              {complianceStatus.compliance.coi?.received && !complianceStatus.compliance.coi?.is_expired 
                                ? `COI is on file for ${complianceStatus.compliance.coi.contractor_name || 'this contractor'}.`
                                : complianceStatus.compliance.coi?.is_expired 
                                  ? `The Certificate of Insurance for ${complianceStatus.compliance.coi.contractor_name || 'this contractor'} has expired. Please upload a current COI.`
                                  : `A Certificate of Insurance is required for ${complianceStatus.compliance.coi.contractor_name || 'this contractor'} before payment can be processed.`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 space-y-2">
                          <div>
                            <Label className="text-xs text-slate-600 mb-1 block">
                              COI Expiration Date <span className="text-red-600">*</span>
                            </Label>
                            <Input
                              type="date"
                              value={coiExpiration}
                              onChange={(e) => setCoiExpiration(e.target.value)}
                              className="text-xs"
                              min={new Date().toISOString().split('T')[0]}
                              required
                            />
                            <p className="text-[10px] text-slate-500 mt-0.5">Required</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600 mb-1 block">
                              {complianceStatus.compliance.coi?.received ? 'Replace COI Document' : 'Upload COI Document'}
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => setCoiFile(e.target.files?.[0] || null)}
                                className="flex-1 text-xs"
                                disabled={isUploadingCOI}
                              />
                              <Button
                                size="sm"
                                onClick={() => coiFile && handleCOIUpload(coiFile)}
                                disabled={!coiFile || isUploadingCOI}
                                className={complianceStatus.compliance.coi?.received && !complianceStatus.compliance.coi?.is_expired ? "bg-slate-600 hover:bg-slate-700" : "bg-blue-600 hover:bg-blue-700"}
                              >
                                {isUploadingCOI ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-1" />
                                    {complianceStatus.compliance.coi?.received ? 'Replace' : 'Upload'}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500">Accepted: PDF, JPG, PNG</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Lien Waiver Upload Section - Show always for upload or replace */}
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className={`${complianceStatus.compliance.lien_waiver.signed ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-3`}>
                      <div className="flex items-start gap-2 mb-2">
                        {complianceStatus.compliance.lien_waiver.signed ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${complianceStatus.compliance.lien_waiver.signed ? 'text-green-800' : 'text-amber-800'}`}>
                            {complianceStatus.compliance.lien_waiver.signed ? 'Lien Waiver Signed' : 'Lien Waiver Required'}
                          </p>
                          <p className={`text-xs ${complianceStatus.compliance.lien_waiver.signed ? 'text-green-600' : 'text-amber-600'} mt-1`}>
                            {complianceStatus.compliance.lien_waiver.signed 
                              ? `Lien waiver is on file${complianceStatus.compliance.lien_waiver.amount ? ` for $${complianceStatus.compliance.lien_waiver.amount.toLocaleString()}` : ''}. You can replace it or request a new one for a different amount.`
                              : 'Upload a signed lien waiver from the contractor. This can be a scanned paper document.'
                            }
                          </p>
                        </div>
                      </div>
                      
                      {/* Request New Lien Waiver - Only show when one already exists */}
                      {complianceStatus.compliance.lien_waiver.signed && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <RefreshCw className="w-4 h-4 text-blue-600" />
                            <p className="text-sm font-medium text-blue-800">Request New Lien Waiver</p>
                          </div>
                          <p className="text-xs text-blue-600 mb-3">
                            Need a lien waiver for a different payment amount? Request a new one and the contractor will receive an email to sign it.
                          </p>
                          {showNewLienWaiverInput ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-slate-500" />
                                <Input
                                  type="number"
                                  placeholder="Enter payment amount"
                                  value={newLienWaiverAmount}
                                  onChange={(e) => setNewLienWaiverAmount(e.target.value)}
                                  className="flex-1 text-sm"
                                  disabled={isRequestingNewLienWaiver}
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={handleRequestNewLienWaiver}
                                  disabled={!newLienWaiverAmount || isRequestingNewLienWaiver}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  {isRequestingNewLienWaiver ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <ArrowRight className="w-4 h-4 mr-1" />
                                      Send Request
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setShowNewLienWaiverInput(false);
                                    setNewLienWaiverAmount('');
                                  }}
                                  disabled={isRequestingNewLienWaiver}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowNewLienWaiverInput(true)}
                              className="border-blue-300 text-blue-700 hover:bg-blue-100"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Request New Lien Waiver
                            </Button>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-3">
                        <Label className="text-xs text-slate-600 mb-2 block">
                          {complianceStatus.compliance.lien_waiver.signed ? 'Replace Lien Waiver' : 'Upload Signed Lien Waiver'}
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setLienWaiverFile(e.target.files?.[0] || null)}
                            className="flex-1 text-xs"
                            disabled={isUploadingLienWaiver}
                          />
                          <Button
                            size="sm"
                            onClick={() => lienWaiverFile && handleLienWaiverUpload(lienWaiverFile)}
                            disabled={!lienWaiverFile || isUploadingLienWaiver}
                            className={complianceStatus.compliance.lien_waiver.signed ? "bg-slate-600 hover:bg-slate-700" : "bg-amber-600 hover:bg-amber-700"}
                          >
                            {isUploadingLienWaiver ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-1" />
                                {complianceStatus.compliance.lien_waiver.signed ? 'Replace' : 'Upload'}
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Accepted: PDF, JPG, PNG (scanned docs OK)</p>
                      </div>
                    </div>
                  </div>

                  {/* Completion Photos Upload Section - Only show if none uploaded */}
                  {!complianceStatus.compliance.completion_photos.uploaded && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="flex items-start gap-2 mb-2">
                          <Camera className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-purple-800">Completion Photos Required</p>
                            <p className="text-xs text-purple-600 mt-1">
                              Upload photos showing completed work. You can upload multiple photos at once.
                            </p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <Label className="text-xs text-slate-600 mb-2 block">Upload Completion Photos</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
                              className="flex-1 text-xs"
                              disabled={isUploadingPhotos}
                            />
                            <Button
                              size="sm"
                              onClick={() => photoFiles.length > 0 && handlePhotosUpload(photoFiles)}
                              disabled={photoFiles.length === 0 || isUploadingPhotos}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              {isUploadingPhotos ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-1" />
                                  Upload {photoFiles.length > 0 && `(${photoFiles.length})`}
                                </>
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Accepted: JPG, PNG, HEIC (multiple photos OK)</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Inspection (if required) */}
                  {complianceStatus.compliance.inspection.required && (
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {complianceStatus.compliance.inspection.passed ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <ClipboardCheck className="w-4 h-4 text-red-500" />
                        )}
                        <span>City Inspection</span>
                      </div>
                      {complianceStatus.compliance.inspection.passed ? (
                        <span className="text-green-600 text-xs">Passed</span>
                      ) : complianceStatus.compliance_request_sent ? (
                        <span className="text-amber-600 text-xs">Awaiting</span>
                      ) : (
                        <span className="text-red-500 text-xs">Required</span>
                      )}
                    </div>
                  )}

                  {/* Compliance info message - informational only, not blocking */}
                  {hasComplianceIssues && complianceStatus.blockers?.length > 0 && (
                    <Alert className="mt-3 bg-amber-50 border-amber-200">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 text-sm">
                        <strong>Compliance incomplete:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {complianceStatus.blockers.map((blocker, idx) => (
                            <li key={idx}>{blocker}</li>
                          ))}
                        </ul>
                        <p className="mt-2 text-xs text-amber-700">
                          You can record the payment now, but this item cannot be included in a draw request until compliance is completed.
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Request Compliance Documents - with Invoice Amount Input */}
                  {hasComplianceIssues && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      {showInvoiceAmountInput ? (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs text-slate-600">Payment Amount for Lien Waiver</Label>
                            <p className="text-xs text-slate-500 mb-1">
                              Enter the amount you'll pay for this {remaining > newAmount ? 'partial' : 'final'} payment. 
                              The contractor will sign a lien waiver for this amount.
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder={`${newAmount > 0 ? newAmount.toLocaleString() : totalOwed.toLocaleString()}`}
                                value={invoiceAmount}
                                onChange={(e) => setInvoiceAmount(e.target.value)}
                                className="flex-1"
                              />
                            </div>
                            {/* Show what will happen after this payment */}
                            {invoiceAmount && parseFloat(invoiceAmount) > 0 && (
                              <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600">
                                <p>After this payment:</p>
                                <p className="font-medium">
                                  Paid: ${(totalPaid + parseFloat(invoiceAmount)).toLocaleString()} / 
                                  Remaining: ${Math.max(0, totalOwed - totalPaid - parseFloat(invoiceAmount)).toLocaleString()}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowInvoiceAmountInput(false);
                                setInvoiceAmount('');
                              }}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleRequestComplianceDocs}
                              disabled={isRequestingCompliance}
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                              {isRequestingCompliance ? (
                                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</>
                              ) : (
                                'Send Request'
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowInvoiceAmountInput(true);
                            // Default to current payment amount if entered, otherwise fall back to phase cost
                            const defaultAmount = newAmount > 0 ? newAmount : (phase?.actual_cost || phase?.estimated_cost || 0);
                            setInvoiceAmount(String(defaultAmount));
                          }}
                          className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          {complianceStatus?.compliance_request_sent ? 'Resend Compliance Request' : 'Request Compliance Documents'}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* View Compliance Documents Button */}
                  {complianceDocuments && (complianceDocuments.completion_photos?.length > 0 || complianceDocuments.lien_waiver || complianceDocuments.inspection_report) && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowComplianceDocuments(!showComplianceDocuments)}
                        className="w-full border-green-300 text-green-700 hover:bg-green-100"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {showComplianceDocuments ? 'Hide' : 'View'} Uploaded Documents ({
                          (complianceDocuments.completion_photos?.length || 0) + 
                          (complianceDocuments.lien_waiver ? 1 : 0) + 
                          (complianceDocuments.inspection_report ? 1 : 0)
                        })
                      </Button>

                      {/* Documents Display */}
                      {showComplianceDocuments && (
                        <div className="mt-3 space-y-3">
                          {/* Lien Waiver */}
                          {complianceDocuments.lien_waiver && (
                            <div className={`p-3 rounded-lg border ${
                              complianceDocuments.lien_waiver.type === 'e-signed' 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-amber-50 border-amber-200'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FileCheck className={`w-5 h-5 ${
                                    complianceDocuments.lien_waiver.type === 'e-signed' 
                                      ? 'text-green-600' 
                                      : 'text-amber-600'
                                  }`} />
                                  <div>
                                    <p className={`font-medium ${
                                      complianceDocuments.lien_waiver.type === 'e-signed' 
                                        ? 'text-green-800' 
                                        : 'text-amber-800'
                                    }`}>Lien Waiver</p>
                                    <p className={`text-xs ${
                                      complianceDocuments.lien_waiver.type === 'e-signed' 
                                        ? 'text-green-600' 
                                        : 'text-amber-600'
                                    }`}>
                                      {complianceDocuments.lien_waiver.type === 'e-signed' 
                                        ? `E-signed by ${complianceDocuments.lien_waiver.signer_name || 'Contractor'}` 
                                        : '⚠️ Uploaded (Not E-Signed)'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {complianceDocuments.lien_waiver.download_url && (
                                    <a
                                      href={`${process.env.REACT_APP_BACKEND_URL}${complianceDocuments.lien_waiver.download_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-1 text-sm ${
                                        complianceDocuments.lien_waiver.type === 'e-signed' 
                                          ? 'text-green-700 hover:text-green-900' 
                                          : 'text-amber-700 hover:text-amber-900'
                                      }`}
                                    >
                                      <Download className="w-4 h-4" />
                                      View
                                    </a>
                                  )}
                                  {complianceDocuments.lien_waiver.type !== 'e-signed' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleRequestComplianceDocs}
                                      disabled={isRequestingCompliance}
                                      className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                                    >
                                      {isRequestingCompliance ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        'Re-request E-Signature'
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Inspection Report */}
                          {complianceDocuments.inspection_report && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ClipboardCheck className="w-5 h-5 text-blue-600" />
                                  <div>
                                    <p className="font-medium text-blue-800">Inspection Report</p>
                                    <p className="text-xs text-blue-600">{complianceDocuments.inspection_report.filename}</p>
                                  </div>
                                </div>
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}${complianceDocuments.inspection_report.download_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900"
                                >
                                  <Download className="w-4 h-4" />
                                  Download
                                </a>
                              </div>
                            </div>
                          )}

                          {/* Completion Photos */}
                          {complianceDocuments.completion_photos?.length > 0 && (
                            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Camera className="w-5 h-5 text-purple-600" />
                                <p className="font-medium text-purple-800 text-sm">
                                  Completion Photos ({complianceDocuments.completion_photos.length})
                                </p>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                {complianceDocuments.completion_photos.map((photo, idx) => (
                                  <div
                                    key={photo.id}
                                    className="relative bg-slate-100 rounded-lg overflow-hidden border border-slate-200"
                                  >
                                    <div className="aspect-video bg-slate-200">
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${photo.download_url}`}
                                        alt={`Completion photo ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                    <div className="p-2 flex items-center justify-between bg-white">
                                      <span className="text-xs text-slate-600 truncate flex-1">
                                        {photo.filename || `Photo ${idx + 1}`}
                                      </span>
                                      <div className="flex gap-1 ml-2">
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${photo.download_url}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1.5 rounded bg-purple-100 hover:bg-purple-200 text-purple-700"
                                          title="View full size"
                                        >
                                          <Eye className="w-3.5 h-3.5" />
                                        </a>
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${photo.download_url}?download=true`}
                                          download={photo.filename || `photo_${idx + 1}.jpg`}
                                          className="p-1.5 rounded bg-green-100 hover:bg-green-200 text-green-700"
                                          title="Download"
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Payment Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500">Total Owed</p>
              <p className="text-lg font-semibold">${totalOwed.toLocaleString()}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500">Already Paid</p>
              <p className="text-lg font-semibold text-green-600">${totalPaid.toLocaleString()}</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${remaining > 0 ? 'bg-yellow-50' : 'bg-slate-50'}`}>
              <p className="text-xs text-slate-500">Remaining</p>
              <p className={`text-lg font-semibold ${remaining > 0 ? 'text-yellow-600' : 'text-slate-600'}`}>
                ${Math.max(0, remaining).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Projected Total Preview */}
          {newAmount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-center gap-3 text-sm">
                <div className="text-center">
                  <p className="text-xs text-slate-500">Previously Paid</p>
                  <p className="font-semibold text-slate-700">${totalPaid.toLocaleString()}</p>
                </div>
                <Plus className="w-4 h-4 text-blue-500" />
                <div className="text-center">
                  <p className="text-xs text-slate-500">This Payment</p>
                  <p className="font-semibold text-blue-600">${newAmount.toLocaleString()}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-500" />
                <div className="text-center">
                  <p className="text-xs text-slate-500">New Total</p>
                  <p className="font-bold text-green-600">${projectedTotal.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment History */}
          {payments.length > 0 && (
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                <Receipt className="w-4 h-4" /> Payment History ({payments.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {payments.map((p, idx) => (
                  <div key={p.id || idx} className="flex items-center justify-between text-sm bg-slate-50 rounded p-2">
                    {editingPaymentId === p.id ? (
                      /* Edit mode */
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-slate-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingPaymentData.amount}
                          onChange={(e) => setEditingPaymentData({ amount: e.target.value })}
                          className="w-32 h-7 text-sm"
                          autoFocus
                        />
                        <span className="text-slate-500 text-xs">{formatDate(p.payment_date)}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={handleSavePaymentEdit}
                          disabled={isUpdatingPayment}
                        >
                          {isUpdatingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-slate-700"
                          onClick={handleCancelEdit}
                          disabled={isUpdatingPayment}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      /* View mode */
                      <>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">${parseFloat(p.amount).toLocaleString()}</span>
                          <span className="text-slate-500">{formatDate(p.payment_date)}</span>
                          {p.is_final_payment && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              Final
                            </Badge>
                          )}
                          {p.is_additional_payment && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              Additional
                            </Badge>
                          )}
                          {p.payment_method === 'legacy_bypass' && (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                              legacy_bypass
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {p.payment_method !== 'legacy_bypass' && (
                            <Badge variant="outline" className="text-xs">{p.payment_method}</Badge>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                            onClick={() => handleEditPayment(p)}
                            title="Edit payment amount"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeletePayment(p.id)}
                            disabled={isDeletingPayment}
                            title="Delete payment"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Type Selection - Only show when not viewing a paid phase */}
          {!isViewingPaidPhase && (
          <div className="border rounded-lg p-4 bg-slate-50">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Payment Type</Label>
                
                {wasMarkedFinal ? (
                  <div className="space-y-3">
                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 text-sm">
                        This phase was previously marked as &quot;Final&quot;. Adding an additional payment will <strong>add to</strong> the existing total of ${totalPaid.toLocaleString()}, not replace it.
                      </AlertDescription>
                    </Alert>
                    <div className="flex items-center space-x-2 p-3 rounded-lg border bg-white">
                      <Plus className="w-5 h-5 text-blue-500" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">Additional Payment</p>
                        <p className="text-xs text-slate-500">Add to existing total (requires receipt or bypass)</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <RadioGroup value={paymentType} onValueChange={setPaymentType} className="flex gap-4">
                    <div className="flex items-center space-x-2 flex-1">
                      <RadioGroupItem value="partial" id="partial" />
                      <Label htmlFor="partial" className="flex items-center gap-2 cursor-pointer flex-1 p-3 rounded-lg border bg-white hover:bg-slate-50 transition-colors">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <div>
                          <p className="font-medium text-sm">Partial Payment</p>
                          <p className="text-xs text-slate-500">More payments expected</p>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 flex-1">
                      <RadioGroupItem value="final" id="final" />
                      <Label htmlFor="final" className="flex items-center gap-2 cursor-pointer flex-1 p-3 rounded-lg border bg-white hover:bg-slate-50 transition-colors">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <div>
                          <p className="font-medium text-sm">Final Payment</p>
                          <p className="text-xs text-slate-500">Phase complete</p>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                )}
              </div>
          )}

              {/* Receipt Upload Section - Only show when not viewing a paid phase */}
              {!isViewingPaidPhase && (() => {
                const hasExistingInvoice = phase?.has_receipt
                  || (Array.isArray(phase?.receipts) && phase.receipts.length > 0)
                  || !!phase?.receipt_file_id;
                // Already on file → don't make the user upload again (unless it's a
                // post-final-payment audit requirement, which still needs a fresh one).
                if (hasExistingInvoice && !wasMarkedFinal && !receiptFile && !showReceiptUploader) {
                  return (
                    <div className="border rounded-lg p-4" data-testid="invoice-on-file">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm font-medium text-green-700">
                          <CheckCircle2 className="w-4 h-4" /> Invoice / receipt already on file — no need to re-upload
                        </span>
                        <Button variant="outline" size="sm" onClick={() => setShowReceiptUploader(true)} data-testid="attach-another-invoice">
                          <Upload className="w-4 h-4 mr-1" /> Attach another
                        </Button>
                      </div>
                    </div>
                  );
                }
                return (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Upload className="w-4 h-4" /> 
                    {wasMarkedFinal ? 'Receipt Required (Post-Final Payment)' : 'Upload Receipt/Invoice (Optional)'}
                  </Label>
                  {wasMarkedFinal && (
                    <div className="flex items-center gap-2">
                      <Switch checked={skipReceipt} onCheckedChange={setSkipReceipt} />
                      <span className="text-xs text-slate-500">Bypass Requirement</span>
                    </div>
                  )}
                </div>
                
                {(!wasMarkedFinal || !skipReceipt) && (
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    receiptFile ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-blue-300'
                  }`}>
                    {isExtracting ? (
                      <div className="flex items-center justify-center gap-2 text-blue-600">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Extracting data with AI...</span>
                      </div>
                    ) : receiptFile ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <Check className="w-5 h-5" />
                          <span>{receiptFile.name}</span>
                        </div>
                        {extractedData && (
                          <div className="text-sm text-slate-600 bg-white rounded p-2">
                            <Sparkles className="w-4 h-4 inline mr-1 text-purple-500" />
                            AI Extracted: <strong>${extractedData.total_amount?.toLocaleString() || 'N/A'}</strong>
                            {extractedData.date && <span className="ml-2">• {formatDate(extractedData.date)}</span>}
                            {extractedData.vendor_name && <span className="ml-2">• {extractedData.vendor_name}</span>}
                          </div>
                        )}
                        <Button variant="outline" size="sm" onClick={() => { setReceiptFile(null); setExtractedData(null); }}>
                          <X className="w-4 h-4 mr-1" /> Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 mb-2">
                          {wasMarkedFinal ? 'Upload receipt for audit compliance' : 'Upload receipt for AI extraction'}
                        </p>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="receipt-upload"
                        />
                        <label htmlFor="receipt-upload">
                          <Button variant="outline" size="sm" asChild>
                            <span><Upload className="w-4 h-4 mr-2" /> Choose File</span>
                          </Button>
                        </label>
                        <p className="text-xs text-slate-500 mt-2">JPG, PNG, WEBP, or PDF</p>
                      </>
                    )}
                  </div>
                )}
              </div>
              );
              })()}

              {/* Payment Form - Only show when not viewing a paid phase */}
              {!isViewingPaidPhase && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount *</Label>
                  <div className="relative mt-1.5">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      className="pl-10"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Payment Date</Label>
                  <div className="relative mt-1.5">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      className="pl-10"
                      value={paymentData.payment_date}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select 
                    value={paymentData.payment_method} 
                    onValueChange={(v) => setPaymentData({ ...paymentData, payment_method: v })}
                  >
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="ach">ACH/Bank Transfer</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="venmo">Venmo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    className="mt-1.5"
                    placeholder="Invoice #, description..."
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  />
                </div>
              </div>
              )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {/* Legacy Bypass Option */}
          {!isViewingPaidPhase && !showBypassConfirm && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowBypassConfirm(true)}
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 mr-auto"
              data-testid="bypass-compliance-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Mark as Paid (Skip Compliance)
            </Button>
          )}
          
          {/* Bypass Confirmation Form */}
          {showBypassConfirm && (
            <div className="w-full bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900">Skip Compliance Verification</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    This will mark the item as paid without requiring lien waivers or other compliance documents. 
                    Use this for legacy payments made outside the system.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-amber-900">Reason for bypassing *</Label>
                  <Textarea 
                    placeholder="e.g., Payment was made before using DrawReady, contractor paid in cash, etc."
                    value={bypassReason}
                    onChange={(e) => setBypassReason(e.target.value)}
                    className="mt-1 bg-white"
                    rows={2}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setShowBypassConfirm(false);
                      setBypassReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleBypassPayment}
                    disabled={isBypassingPayment || !bypassReason.trim()}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="confirm-bypass-btn"
                  >
                    {isBypassingPayment ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Confirm & Mark as Paid</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {!showBypassConfirm && (
            <>
              <Button variant="outline" onClick={onClose}>{isViewingPaidPhase ? 'Close' : 'Cancel'}</Button>
              {!isViewingPaidPhase && (
              <Button 
                onClick={handleSubmit} 
                disabled={isLoading || !paymentData.amount || (wasMarkedFinal && !receiptFile && !skipReceipt)}
                className={
                  wasMarkedFinal 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : paymentType === 'final' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                }
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Logging...</>
                ) : wasMarkedFinal ? (
                  <><Plus className="w-4 h-4 mr-2" /> Add Payment (${newAmount.toLocaleString()})</>
                ) : paymentType === 'final' ? (
                  <><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as Final (${newAmount.toLocaleString()})</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Log Payment (${newAmount.toLocaleString()})</>
                )}
              </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
